import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, BadgePercent, Film, ImageIcon, Loader2, Pencil, Plus, Power, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const EMPTY_FORM = { title: "", description: "", badge: "", media_type: "image", media_url: "", cta_label: "Shop now", cta_link: "/shop", active: false };

export default function AdminOffers() {
  const [offers, setOffers] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [frequency, setFrequency] = useState("daily");
  const [intervalHours, setIntervalHours] = useState("3");
  const [freqSaving, setFreqSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/offers/config").then(({ data }) => {
      setFrequency(data.frequency || "daily");
      setIntervalHours(String(data.interval_hours ?? 3));
    }).catch(() => {});
  }, []);

  const changeFrequency = async (f, hours) => {
    setFrequency(f);
    if (hours !== undefined) setIntervalHours(hours);
    const h = parseFloat(hours !== undefined ? hours : intervalHours);
    if (f === "interval" && !(h > 0)) { toast.error("Set the hours first"); return; }
    setFreqSaving(true);
    try {
      await api.put("/admin/offers-settings", { frequency: f, interval_hours: h || 3 });
      toast.success(f === "daily" ? "Popup shows once a day per visitor" : f === "every_visit" ? "Popup shows on every homepage visit" : `Popup reappears every ${h} hour${h === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't save frequency"));
    } finally {
      setFreqSaving(false);
    }
  };

  const load = useCallback(() => {
    api.get("/admin/offers")
      .then(({ data }) => setOffers(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load offers")));
  }, []);
  useEffect(load, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ title: o.title, description: o.description, badge: o.badge, media_type: o.media_type, media_url: o.media_url, cta_label: o.cta_label, cta_link: o.cta_link, active: o.active });
    setModalOpen(true);
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = form.media_type === "video" ? "/admin/uploads/video" : "/admin/uploads";
      const { data } = await api.post(url, fd);
      setForm((f) => ({ ...f, media_url: data.url }));
      toast.success("Media uploaded");
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.cta_link && /^https?:\/\//.test(form.cta_link)) toast("External link — opens in a new tab for visitors");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/offers/${editing.id}`, form);
      else await api.post("/admin/offers", form);
      toast.success(editing ? "Offer updated" : "Offer created");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save offer"));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/admin/offers/${o.id}/toggle`);
      toast.success(data.active ? `"${o.title}" is live` : `"${o.title}" switched off`);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't toggle")); }
    finally { setBusyId(null); }
  };

  const remove = async (o) => {
    setBusyId(o.id);
    try {
      await api.delete(`/admin/offers/${o.id}`);
      toast.success("Offer deleted");
      setConfirmId(null);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't delete")); }
    finally { setBusyId(null); }
  };

  const move = async (idx, dir) => {
    const next = [...offers];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOffers(next);
    try { await api.post("/admin/offers/reorder", { ids: next.map((o) => o.id) }); }
    catch { toast.error("Couldn't save order"); load(); }
  };

  return (
    <div className="space-y-5" data-testid="admin-offers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-white">Offers Spotlight</h1>
          <p className="mt-1 text-sm text-neutral-500">Live offers appear in the homepage popup — {frequency === "every_visit" ? "on every homepage visit" : frequency === "interval" ? `every ${intervalHours} hour${parseFloat(intervalHours) === 1 ? "" : "s"} per visitor` : "once a day per visitor"}. Switch on/off anytime.</p>
        </div>
        <button data-testid="offers-new-btn" onClick={openNew} className="flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New offer
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Popup frequency</p>
        <div className="mt-2.5 grid gap-2 sm:grid-cols-3">
          {[
            ["daily", "Once a day per visitor", "Shows on their first homepage visit each day — never nags"],
            ["every_visit", "Every homepage visit", "Shows every time someone opens the homepage"],
            ["interval", "Every few hours", "Shows once, then stays quiet for the hours you set"],
          ].map(([v, label, hint]) => (
            <button
              key={v}
              data-testid={`offers-frequency-${v}`}
              onClick={() => changeFrequency(v)}
              disabled={freqSaving}
              className={`rounded-xl border px-4 py-3 text-left transition-colors disabled:opacity-50 ${frequency === v ? "border-amber-500/60 bg-amber-500/10" : "border-white/10 bg-black/30 hover:border-white/25"}`}
            >
              <span className={`flex items-center gap-2 text-sm font-bold ${frequency === v ? "text-amber-400" : "text-white"}`}>
                {freqSaving && frequency === v ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span className={`h-2 w-2 rounded-full ${frequency === v ? "bg-amber-400" : "bg-neutral-600"}`} />}
                {label}
              </span>
              <span className="mt-1 block text-xs text-neutral-500">{hint}</span>
              {v === "interval" && frequency === "interval" && (
                <span className="mt-2.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number" min="0.25" max="720" step="0.25"
                    value={intervalHours}
                    onChange={(e) => setIntervalHours(e.target.value)}
                    onBlur={() => changeFrequency("interval")}
                    className="h-8 w-20 rounded-lg border border-white/10 bg-black/30 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none"
                    data-testid="offers-interval-hours"
                  />
                  <span className="text-xs text-neutral-500">hours between popups</span>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {offers === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : offers.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
          <BadgePercent className="mx-auto h-10 w-10 text-neutral-600" />
          <p className="mt-3 text-sm text-neutral-400">No offers yet. Create one and switch it on — the homepage popup appears only when at least one offer is live.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {offers.map((o, i) => (
            <div key={o.id} data-testid={`offer-row-${o.id}`} className={`flex items-center gap-4 rounded-2xl border p-4 transition-colors ${o.active ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
              <div className="flex flex-col gap-1">
                <button data-testid={`offer-up-${o.id}`} onClick={() => move(i, -1)} disabled={i === 0} className="rounded p-1 text-neutral-500 hover:text-white disabled:opacity-25" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
                <button data-testid={`offer-down-${o.id}`} onClick={() => move(i, 1)} disabled={i === offers.length - 1} className="rounded p-1 text-neutral-500 hover:text-white disabled:opacity-25" aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
              </div>
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/50">
                {o.media_url && o.media_type === "image" ? <img src={o.media_url} alt="" className="h-full w-full object-cover" /> : o.media_type === "video" ? <Film className="h-5 w-5 text-neutral-500" /> : <ImageIcon className="h-5 w-5 text-neutral-500" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-sm font-bold text-white">{o.title}</p>
                  {o.badge && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-400">{o.badge}</span>}
                  {/^https?:\/\//.test(o.cta_link) && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-400">external link</span>}
                </div>
                <p className="mt-0.5 truncate text-xs text-neutral-500">{o.description || "—"} · CTA: {o.cta_label} → {o.cta_link}</p>
              </div>
              <span data-testid={`offer-state-${o.id}`} className={`hidden rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider sm:inline ${o.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-neutral-500"}`}>
                {o.active ? "Live" : "Off"}
              </span>
              <div className="flex items-center gap-1.5">
                <button data-testid={`offer-toggle-${o.id}`} onClick={() => toggle(o)} disabled={busyId === o.id} className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${o.active ? "bg-white/10 text-neutral-300 hover:bg-white/15" : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"}`}>
                  {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
                  {o.active ? "Switch off" : "Go live"}
                </button>
                <button data-testid={`offer-edit-${o.id}`} onClick={() => openEdit(o)} className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-white/10 hover:text-white" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`offer-delete-${o.id}`} onClick={() => setConfirmId(o.id)} className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-red-500/15 hover:text-red-400" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* create/edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setModalOpen(false)}>
          <div data-testid="offer-modal" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#171814] p-6" onClick={(e) => e.stopPropagation()} data-lenis-prevent>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-white">{editing ? "Edit offer" : "New offer"}</h2>
              <button onClick={() => setModalOpen(false)} className="rounded-full p-1.5 text-neutral-400 hover:bg-white/10 hover:text-white" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">Title *</label>
                <input data-testid="offer-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Diwali Bulk Bonanza" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">Short description</label>
                <input data-testid="offer-form-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One line shown under the headline" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">Badge text</label>
                <input data-testid="offer-form-badge" className={inputCls} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder='e.g. "58% OFF" or "Limited period"' />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">Media</label>
                <div className="mb-2 flex gap-2">
                  {[["image", "Image", ImageIcon], ["video", "Video", Film]].map(([v, label, Icon]) => (
                    <button key={v} type="button" data-testid={`offer-form-media-${v}`} onClick={() => setForm({ ...form, media_type: v, media_url: "" })} className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${form.media_type === v ? "bg-amber-500 text-black" : "bg-white/10 text-neutral-300 hover:bg-white/15"}`}>
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                <input ref={fileRef} type="file" accept={form.media_type === "video" ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp"} className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0])} />
                <div className="flex items-center gap-3">
                  <button type="button" data-testid="offer-form-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-white/15 disabled:opacity-50">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    {uploading ? "Uploading…" : form.media_type === "video" ? "Upload video (MP4/WebM, ≤50 MB)" : "Upload image (≤5 MB)"}
                  </button>
                  {form.media_url && <span className="truncate text-xs text-emerald-400">✓ attached</span>}
                </div>
                {form.media_url && form.media_type === "image" && <img src={form.media_url} alt="" className="mt-3 h-28 w-full rounded-xl object-cover" />}
                {form.media_url && form.media_type === "video" && <video src={form.media_url} className="mt-3 h-28 w-full rounded-xl object-cover" muted loop autoPlay playsInline />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">CTA label</label>
                  <input data-testid="offer-form-cta-label" className={inputCls} value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Shop the deals" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">CTA link</label>
                  <input data-testid="offer-form-cta-link" className={inputCls} value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/shop?offers=1" />
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <input type="checkbox" data-testid="offer-form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-amber-500" />
                <span className="text-sm text-neutral-300">Switch on immediately (visible to all visitors)</span>
              </label>
              <button data-testid="offer-form-save" onClick={save} disabled={saving || uploading} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Create offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete confirm */}
      {confirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setConfirmId(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#171814] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold text-white">Delete this offer?</h3>
            <p className="mt-2 text-sm text-neutral-400">It disappears from the popup immediately. This can't be undone.</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmId(null)} className="flex-1 rounded-full bg-white/10 py-2.5 text-sm font-bold text-white hover:bg-white/15">Cancel</button>
              <button data-testid="offer-delete-confirm" onClick={() => remove(offers.find((o) => o.id === confirmId))} className="flex-1 rounded-full bg-red-500 py-2.5 text-sm font-bold text-white hover:bg-red-400">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
